import { Button, Space, Table, message } from 'antd';
import { useEffect, useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { ApiError } from '../../lib/http';
import { RejectDrawer } from '../../components/RejectDrawer';
import { StatusBadge } from '../../components/StatusBadge';
import { MISSING_REASON, rejectReasonLabel } from '../../lib/status';
import { requestsApi } from '../../services/resources';
import type { ExperimentRequest, RejectReasonType } from '../../types/api';

export function AdminRequestsPage() {
  const [items, setItems] = useState<ExperimentRequest[]>([]);
  const [error, setError] = useState('');
  const [target, setTarget] = useState<ExperimentRequest | null>(null);

  async function load() {
    try {
      const page = await requestsApi.list({ page: 1, limit: 50 });
      const sorted = [...page.items].sort((a, b) => Number(b.sla_overdue) - Number(a.sla_overdue));
      setItems(sorted);
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? `列表加载失败：${e.message}` : '列表加载失败，请检查网络后重试');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function reject(reason_code: RejectReasonType, reason_detail: string) {
    if (!target) return;
    await requestsApi.reject(target.id, reason_code, reason_detail);
    setTarget(null);
    await load();
  }

  return (
    <div className="stack">
      <h1 className="page-title">学生申请</h1>
      {error ? (
        <EmptyState text={error} action={{ label: '重新加载', onClick: () => void load() }} />
      ) : items.length === 0 ? (
        <EmptyState text="没有待审学生申请。" />
      ) : (
        <Table
          scroll={{ x: 960 }}
          rowKey="id"
          dataSource={items}
          pagination={false}
          columns={[
            { title: '申请人', dataIndex: 'user_name', width: 90, render: (v: string | null) => v || '—' },
            {
              title: '班级 / 教研组',
              dataIndex: 'class_or_group',
              width: 110,
              render: (v: string | null) => v || '—',
            },
            { title: '课题', dataIndex: 'title', width: 200 },
            {
              title: '截止',
              width: 200,
              render: (_, r) => (
                <span>
                  {String(r.due_at).slice(0, 10)} 截止 · 2 个工作日内 {r.sla_overdue ? '（已超期）' : ''}
                </span>
              ),
            },
            {
              title: '状态',
              render: (_, r) => <StatusBadge status={r.status} reasonType={r.reason_type} />,
            },
            {
              title: '原因',
              render: (_, r) =>
                r.status === 'rejected'
                  ? [
                      rejectReasonLabel(r.reject_reason_code ?? r.reason_type),
                      r.reject_reason_detail,
                    ]
                      .filter(Boolean)
                      .join('：') || MISSING_REASON
                  : null,
            },
            {
              title: '操作',
              render: (_, r) =>
                r.status === 'pending' ? (
                  <Space>
                    <Button
                      className="touch"
                      type="primary"
                      onClick={async () => {
                        try {
                          await requestsApi.approve(r.id);
                          await load();
                        } catch (e) {
                          message.error(e instanceof Error ? e.message : '操作失败');
                        }
                      }}
                    >
                      通过
                    </Button>
                    <Button className="touch" danger onClick={() => setTarget(r)}>
                      驳回
                    </Button>
                  </Space>
                ) : null,
            },
          ]}
        />
      )}
      <RejectDrawer
        open={!!target}
        onClose={() => setTarget(null)}
        onSubmit={({ reason_code, reason_detail }) => reject(reason_code, reason_detail)}
      />
    </div>
  );
}
